/**
 * Shared FOMO date utilities
 *
 * All pricing pages import from here so the offer-end date auto-rolls
 * to the last day of the current month without any manual edits.
 *
 * May  → "May 31"
 * June → "June 30"
 * etc.
 */

/** Returns a Date object set to midnight on the last day of the current month. */
function offerEndDate() {
  const d = new Date();
  d.setMonth(d.getMonth() + 1, 0); // day 0 of next month = last day of this month
  d.setHours(23, 59, 59, 0);
  return d;
}

/**
 * Human-readable offer end label, e.g. "May 31" or "June 30".
 * Automatically advances to the next month on the 1st.
 */
export function offerEndLabel() {
  return offerEndDate().toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
  });
}

/**
 * Number of full days remaining until the end of the current month.
 * Returns 0 on the last day.
 */
export function daysUntilOfferEnd() {
  const now = new Date();
  const end = offerEndDate();
  const msLeft = end.getTime() - now.getTime();
  return Math.max(0, Math.ceil(msLeft / (1000 * 60 * 60 * 24)));
}

/**
 * Short urgency label shown next to countdown, e.g.:
 *   "31 days left"  → full month
 *   "3 days left"   → last few days
 *   "Today only!"   → last day
 */
export function daysLeftLabel() {
  const d = daysUntilOfferEnd();
  if (d === 0) return "Today only!";
  if (d === 1) return "1 day left!";
  return `${d} days left`;
}

/**
 * Auto-inject FOMO fields onto any non-custom tier that doesn't already
 * have them.  originalPrice = 2× current price  |  limitedTime = true
 *
 * Safe to call in any component — custom / enterprise tiers are untouched.
 */
export function withFomo(tiers = []) {
  return tiers.map((p) => {
    const isCustom =
      /custom/i.test((p?.price || "").trim()) ||
      /enterprise|custom/i.test(p?.tier || "");
    if (isCustom || p.originalPrice) return p; // already set or custom → skip
    const n = parseInt((p?.price || "").replace(/[^0-9]/g, ""), 10) || 0;
    if (!n) return p;
    return {
      ...p,
      originalPrice: `$${n * 2}`,
      limitedTime: true,
    };
  });
}
